import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { NewsItem } from "@/types/content";
import { formatDate } from "@/lib/content";

interface HeroCarouselProps {
  items: NewsItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => {
      setActive((v) => (v + 1) % items.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) {
    return (
      <div className="w-full h-[420px] md:h-[560px] bg-background-100 flex items-center justify-center">
        <p className="text-foreground-500">暂无焦点内容，稍后再来</p>
      </div>
    );
  }

  const go = (dir: number) => {
    setActive((v) => (v + dir + items.length) % items.length);
  };

  return (
    <section className="relative w-full h-[460px] md:h-[580px] overflow-hidden bg-foreground-950">
      {items.map((item, idx) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            idx === active ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={item.cover}
            alt={`${item.title} - 小蜜桃焦点内容`}
            title={`${item.title} - 小蜜桃`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/45 to-foreground-950/25"></div>
        </div>
      ))}

      <div className="relative z-10 h-full w-full px-4 md:px-6 flex items-end pb-12 md:pb-20">
        <div className="w-full max-w-3xl">
          <span className="inline-flex items-center gap-2 text-xs md:text-sm text-background-50 bg-primary-500 px-3 py-1 rounded-full">
            <i className="ri-fire-line"></i>
            焦点推荐 · {items[active].categoryName}
          </span>
          <h1 className="mt-4 font-heading font-bold text-2xl md:text-4xl lg:text-5xl text-background-50 leading-tight">
            {items[active].title}
          </h1>
          <p className="mt-4 text-sm md:text-base text-background-100/90 max-w-2xl line-clamp-2">
            {items[active].summary}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${items[active].id}`}
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
            >
              阅读全文
              <i className="ri-arrow-right-line"></i>
            </Link>
            <span className="text-xs text-background-100/80">
              {items[active].author} · {formatDate(items[active].publishDate)}
            </span>
          </div>
        </div>
      </div>

      {items.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => go(-1)}
            aria-label="上一张"
            className="hidden md:flex absolute left-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-background-50/20 hover:bg-background-50/35 backdrop-blur text-background-50 items-center justify-center transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            aria-label="下一张"
            className="hidden md:flex absolute right-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-background-50/20 hover:bg-background-50/35 backdrop-blur text-background-50 items-center justify-center transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>

          <div className="absolute bottom-6 right-4 md:right-6 z-20 flex items-center gap-2">
            {items.map((item, idx) => (
              <button
                key={item.id}
                type="button"
                aria-label={`切换到第 ${idx + 1} 张`}
                onClick={() => setActive(idx)}
                className={`h-1.5 rounded-full transition-all cursor-pointer ${
                  idx === active ? "w-7 bg-primary-500" : "w-3 bg-background-50/50"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}